import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';

import type { Team, TeamMember, TeamRole } from '@/shared/types/team';
import { sbq, throwOnError } from '@/shared/utils/supabaseQuery';

export const teamsApi = createApi({
  reducerPath: 'teamsApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['Team', 'TeamMember'],
  endpoints: (builder) => ({
    getMyTeams: builder.query<Team[], void>({
      queryFn: sbq(async (sb) => {
        const userId = (await sb.auth.getUser()).data.user?.id;
        if (!userId) throw new Error('Not authenticated');
        const memberships = throwOnError(
          await sb.from('team_members').select('team_id').eq('user_id', userId)
        ) as any[];
        const teamIds = memberships.map((m: any) => m.team_id);
        if (teamIds.length === 0) return [] as unknown as Team[];
        const teams = throwOnError(
          await sb
            .from('teams')
            .select('*')
            .in('id', teamIds)
            .order('updated_at', { ascending: false })
        ) as any[];
        return teams as Team[];
      }, 'Teams'),
      providesTags: ['Team'],
    }),

    createTeam: builder.mutation<Team, { name: string; description?: string }>({
      queryFn: sbq(async (sb, { name, description }) => {
        const userId = (await sb.auth.getUser()).data.user?.id;
        if (!userId) throw new Error('Not authenticated');
        console.log(`Creating team: ${name}, description: ${description}, userId: ${userId}`);
        const result = throwOnError(
          await sb
            .from('teams')
            .insert([{ name, description: description ?? null, created_by: userId }])
            .select('*')
            .single()
        );
        return result as Team;
      }, 'Team'),
      invalidatesTags: ['Team'],
    }),

    updateTeam: builder.mutation<Team, { id: string; name?: string; description?: string }>({
      queryFn: sbq(async (sb, { id, ...fields }) => {
        const result = throwOnError(
          await sb
            .from('teams')
            .update({ ...fields, updated_at: new Date().toISOString() })
            .eq('id', id)
            .select('*')
            .single()
        );
        return result as Team;
      }, 'Team'),
      invalidatesTags: ['Team'],
    }),

    deleteTeam: builder.mutation<void, string>({
      queryFn: sbq(async (sb, id) => {
        const { error } = await sb.from('teams').delete().eq('id', id);
        if (error) throw error;
        return undefined;
      }, 'Team'),
      invalidatesTags: ['Team'],
    }),

    getTeamMembers: builder.query<TeamMember[], string>({
      queryFn: sbq(async (sb, teamId) => {
        const members = throwOnError(
          await sb
            .from('team_members')
            .select('*')
            .eq('team_id', teamId)
            .order('created_at', { ascending: true })
        ) as any[];
        return members as TeamMember[];
      }, 'Team Members'),
      providesTags: ['TeamMember'],
    }),

    addTeamMember: builder.mutation<
      TeamMember,
      { team_id: string; user_id: string; role?: TeamRole }
    >({
      queryFn: sbq(async (sb, { team_id, user_id, role = 'member' }) => {
        const result = throwOnError(
          await sb.from('team_members').insert([{ team_id, user_id, role }]).select('*').single()
        );
        return result as TeamMember;
      }, 'Team Member'),
      invalidatesTags: ['TeamMember'],
    }),

    updateTeamMember: builder.mutation<TeamMember, { id: string; role: TeamRole }>({
      queryFn: sbq(async (sb, { id, role }) => {
        const result = throwOnError(
          await sb.from('team_members').update({ role }).eq('id', id).select('*').single()
        );
        return result as TeamMember;
      }, 'Team Member'),
      invalidatesTags: ['TeamMember'],
    }),

    removeTeamMember: builder.mutation<void, string>({
      queryFn: sbq(async (sb, id) => {
        const { error } = await sb.from('team_members').delete().eq('id', id);
        if (error) throw error;
        return undefined;
      }, 'Team Member'),
      invalidatesTags: ['TeamMember'],
    }),
  }),
});

export const {
  useGetMyTeamsQuery,
  useCreateTeamMutation,
  useUpdateTeamMutation,
  useDeleteTeamMutation,
  useGetTeamMembersQuery,
  useAddTeamMemberMutation,
  useUpdateTeamMemberMutation,
  useRemoveTeamMemberMutation,
} = teamsApi;
